import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  fetchEmployeeDetails,
  saveFormData,
  submitForm,
  clearForm
} from "../../../store/slices/Citizenship/citizenshipFormSlice";
import ApplyForm from "./ApplyForm";
import Loader from "../../../components/common/Loader";
import { useNavigate } from "react-router-dom";
import {
  CitizenshipFormData,
} from "../../../config/citizenshipFormConfig";
import { trackFormSubmit, trackFormSave, trackButtonClick, trackNavigation } from '../../../services/analytics';

const LOCAL_STORAGE_KEY = "citizenshipFormSaved";

interface ApplyContainerProps {
  empId: string;
  empNuid: string;
  altPhone: string;
  altEmail: string;
}

const ApplyContainer: React.FC<ApplyContainerProps> = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState<string>("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    data: formData,
    loading,
    error,
    savePatchError,
  } = useAppSelector((state) => state.citizenshipForm);
  const hasEmployeeDetails = Object.keys(formData ?? {}).length > 0;

  useEffect(() => {
    if (!hasEmployeeDetails && !loading && !error) {
      dispatch(fetchEmployeeDetails());
    }
  }, [dispatch, hasEmployeeDetails, loading, error]);


  const getInitialValues = () => {
    const saved = sessionStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        return { ...formData, ...JSON.parse(saved) };
      } catch {
        return formData;
      }
    }
    return formData;
  };

  const initialValues = useMemo(() => getInitialValues(), [formData]);
  
 
  const convertDateFormat = (dateString: string | undefined | null): string => {
    if (!dateString || typeof dateString !== 'string') {
      return '';
    }
   
  let month, day, year;

  if (dateString.includes("/")) {
    [month, day, year] = dateString.split("/");
  } else if (dateString.includes("-")) {
    [year, month, day] = dateString.split("-");
  } else {
    return "";
  }
   // Pad month and day to 2 digits
  const mm = month.padStart(2, "0");
  const dd = day.padStart(2, "0");
 
  return `${mm}-${dd}-${year}`;
  };
   
  const handleSubmit = async (formData: CitizenshipFormData) => {
    
    const submitPayload: Record<string, any> = {
      empId: formData.empId,
    };
    if (formData.filingFee)  submitPayload.filingFee = formData.filingFee;
    if (formData.n400ApplicationDate) {
      const convertedDate = convertDateFormat(formData.n400ApplicationDate);
      if (convertedDate) submitPayload.n400ApplicationDate = convertedDate;
    }
    if (formData.biometricsFee) submitPayload.biometricsFee = formData.biometricsFee;
    if (formData.totalClassFees) submitPayload.totalClassFees = formData.totalClassFees;
    if (formData.classSchoolName) submitPayload.classSchoolName = formData.classSchoolName;
    if (formData.classStartDate) {
      const convertedDate = convertDateFormat(formData.classStartDate);
      if (convertedDate) submitPayload.classStartDate = convertedDate;
    }
    if (formData.classEndDate) {
      const convertedDate = convertDateFormat(formData.classEndDate);
      if (convertedDate) submitPayload.classEndDate = convertedDate;
    }
    if (formData.comments) submitPayload.comments = formData.comments;

    const result = await dispatch(submitForm(submitPayload));
   // Only show success if the API call was successful
    if (submitForm.fulfilled.match(result)) {
      const appId = result.payload?.applicationId || "";
      console.log(`📊 Analytics: Citizenship form submitted - Application ${appId}`);
      trackFormSubmit('citizenship', undefined, { applicationId: appId });
      setApplicationId(appId);
      setShowSuccess(true);
    }
  };

  
  const handleSave = async (values: Partial<CitizenshipFormData>) => {
    const payload: any = {
      empId: values.empId || null,
      altPhone: values.altPhone || null,
      altEmail: values.altEmail || null,
    };
    sessionStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values));
    console.log('📊 Analytics: Citizenship form saved');
    trackFormSave('citizenship');
    await dispatch(saveFormData(payload));
  };

  const handleCloseSuccess = () => {
    console.log('📊 Analytics: Close success - navigating to citizenship');
    trackButtonClick('Close', 'citizenship-apply-success');
    trackNavigation('citizenship-apply', 'citizenship', 'next');
    setShowSuccess(false);
    navigate("/citizenship");
    dispatch(clearForm());
  };

  return (
    <>
      {loading && <Loader />}
      <ApplyForm
        initialValues={initialValues}
        showSuccess={showSuccess}
        applicationId={applicationId}
        error={error}
        savePatchError={savePatchError}
        onCloseSuccess={handleCloseSuccess}
        handleSubmit={handleSubmit}
        handleSave={handleSave}
      />
    </>
  );
};

export default ApplyContainer;
